import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Clock, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/site/SEO";
import { COMPANY, SERVICES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", service: "", location: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Enquiry sent — we'll be in touch.");
      setSent(true);
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please phone us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const wa = COMPANY.whatsapp.replace(/[^0-9]/g, "");

  return (
    <>
      <SEO
        title="Contact — L F Building & Maintenance Ltd"
        description="Get in touch for a free, no-obligation quote. Serving Essex, Suffolk and surrounding areas. Phone, email or WhatsApp."
        path="/contact"
      />

      <section className="bg-brand-ink text-white pt-28 md:pt-40 pb-20 md:pb-28 border-b border-white/10">
        <div className="container-lf grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9 space-y-6">
            <span className="eyebrow text-brand">Contact</span>
            <h1 className="font-display font-bold uppercase text-6xl md:text-8xl leading-[0.9]">
              Let's <span className="text-brand">talk.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              Ready for a free quote? Send an enquiry below or contact us directly by phone, email or WhatsApp. We
              reply within one working day.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="container-lf grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* CONTACT INFO */}
          <aside className="lg:col-span-5 space-y-8" data-testid="contact-info">
            <div>
              <span className="eyebrow">Direct Lines</span>
              <h2 className="font-display font-bold uppercase text-4xl mt-4">Get in touch<br />however you like.</h2>
            </div>

            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="contact-phone"
               className="block border border-black/10 hover:border-brand p-6 transition-colors group">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand text-white grid place-items-center shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">Call us</div>
                  <div className="font-display font-bold text-2xl mt-1 group-hover:text-brand transition-colors">{COMPANY.phone}</div>
                  <div className="text-sm text-neutral-500 mt-1">{COMPANY.hours}</div>
                </div>
              </div>
            </a>

            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email"
               className="block border border-black/10 hover:border-brand p-6 transition-colors group">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand text-white grid place-items-center shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">Email us</div>
                  <div className="font-display font-bold text-xl mt-1 group-hover:text-brand transition-colors break-all">{COMPANY.email}</div>
                  <div className="text-sm text-neutral-500 mt-1">Replies within one working day</div>
                </div>
              </div>
            </a>

            <a
              href={`https://wa.me/${wa}?text=${encodeURIComponent("Hi L F Building, I'd like to enquire about a project.")}`}
              target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
              className="block border border-black/10 hover:border-[#25D366] p-6 transition-colors group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#25D366] text-white grid place-items-center shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">WhatsApp</div>
                  <div className="font-display font-bold text-xl mt-1 group-hover:text-[#25D366] transition-colors">{COMPANY.whatsappDisplay}</div>
                  <div className="text-sm text-neutral-500 mt-1">Send photos of the job direct</div>
                </div>
              </div>
            </a>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-black/10">
              <div>
                <MapPin className="w-4 h-4 text-brand mb-2" />
                <div className="text-xs uppercase tracking-widest text-neutral-500">Areas</div>
                <div className="text-sm font-semibold mt-1">{COMPANY.areas}</div>
              </div>
              <div>
                <Clock className="w-4 h-4 text-brand mb-2" />
                <div className="text-xs uppercase tracking-widest text-neutral-500">Hours</div>
                <div className="text-sm font-semibold mt-1">{COMPANY.hours}</div>
              </div>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="border border-black/10 p-6 md:p-10 bg-white">
              <div className="mb-8">
                <span className="eyebrow">Enquiry Form</span>
                <h2 className="font-display font-bold uppercase text-4xl mt-4">Request a free quote.</h2>
                <p className="text-neutral-600 text-sm mt-2">Fields marked * are required.</p>
              </div>

              {sent ? (
                <div className="bg-brand text-white p-8 text-center" data-testid="contact-success">
                  <div className="font-display font-bold uppercase text-3xl">Thanks — we've got it.</div>
                  <p className="text-white/80 mt-2">We'll be in touch within one working day.</p>
                  <Button
                    className="rounded-none bg-white text-brand hover:bg-brand-ink hover:text-white h-11 px-6 mt-6 uppercase tracking-wider text-sm font-semibold"
                    onClick={() => setSent(false)}
                    data-testid="contact-send-another"
                  >
                    Send another <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="uppercase text-xs tracking-widest">Name *</Label>
                    <Input id="name" data-testid="contact-input-name" value={form.name} onChange={set("name")}
                           className="rounded-none border-black/20 focus-visible:ring-brand h-12 mt-2" placeholder="Your full name" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="uppercase text-xs tracking-widest">Email *</Label>
                    <Input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")}
                           className="rounded-none border-black/20 focus-visible:ring-brand h-12 mt-2" placeholder="you@email.com" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="uppercase text-xs tracking-widest">Phone *</Label>
                    <Input id="phone" type="tel" data-testid="contact-input-phone" value={form.phone} onChange={set("phone")}
                           className="rounded-none border-black/20 focus-visible:ring-brand h-12 mt-2" placeholder="07…" />
                  </div>
                  <div>
                    <Label htmlFor="location" className="uppercase text-xs tracking-widest">Location</Label>
                    <Input id="location" data-testid="contact-input-location" value={form.location} onChange={set("location")}
                           className="rounded-none border-black/20 focus-visible:ring-brand h-12 mt-2" placeholder="Town / postcode" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="uppercase text-xs tracking-widest">Service Required *</Label>
                    <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                      <SelectTrigger data-testid="contact-select-service" className="rounded-none border-black/20 focus:ring-brand h-12 mt-2">
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {SERVICES.map((s) => (
                          <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>
                        ))}
                        <SelectItem value="Other / General">Other / General Enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="message" className="uppercase text-xs tracking-widest">Project Details *</Label>
                    <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={set("message")}
                              rows={6} className="rounded-none border-black/20 focus-visible:ring-brand mt-2" placeholder="Tell us about your project — timeline, scope, questions…" />
                  </div>
                  <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                    <p className="text-xs text-neutral-500 max-w-sm">
                      By submitting you agree we may contact you regarding your enquiry. We never share your details.
                    </p>
                    <Button type="submit" disabled={submitting} data-testid="contact-submit"
                            className="rounded-none bg-brand hover:bg-brand-hover text-white h-13 px-8 uppercase tracking-wider font-semibold min-w-[220px] h-14">
                      {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<>Send Enquiry <ArrowRight className="w-4 h-4 ml-2" /></>)}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
